import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Footer from './Footer';
import ImpersonateBanner from './ImpersonateBanner';
import secuLogo from '../assets/secu-logo.svg';
import secuLogoDark from '../assets/secu-logo-dark.svg';
import { NavIcon, SvgMail, SvgShield, SvgNetwork, SvgDoc, SvgBot, SvgSettings, SvgUser, SvgBan, SvgLog } from './icons/NavIcons';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { isAiChatEnabled, currentTheme, designVersion } = useSettings();
  const isV2 = designVersion === 'v2';
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRound, setSelectedRound] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [adminMenuExpanded, setAdminMenuExpanded] = useState<boolean>(false);
  
  // PDF 모드 확인
  const urlParams = new URLSearchParams(window.location.search);
  const isPdfMode = urlParams.get('pdf') === 'true';

  // 관리자 페이지인지 확인
  const isAdminPage = location.pathname === '/admin';
  const adminCategory = isAdminPage ? (urlParams.get('category') || 'USER') : null;

  useEffect(() => {
    // localStorage에서 선택된 라운드 복원
    const savedRound = localStorage.getItem('selectedRound');
    if (savedRound) {
      setSelectedRound(savedRound);
    }
    // 관리자 페이지일 때 메뉴 자동 확장
    if (isAdminPage) {
      setAdminMenuExpanded(true);
    }
  }, [isAdminPage]);

  useEffect(() => {
    // 선택된 라운드를 localStorage에 저장
    if (selectedRound) {
      localStorage.setItem('selectedRound', selectedRound);
    } else {
      localStorage.removeItem('selectedRound');
    }
  }, [selectedRound]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`layout ${isPdfMode ? 'pdf-mode' : ''}`}>
      {!isPdfMode && <ImpersonateBanner />}
      {!isPdfMode && (
        <header className="header">
          <div className="header-content">
            <h1 className="logo">
              <img
                src={currentTheme === 'dark' ? secuLogoDark : secuLogo}
                alt="SecuCenter"
                className="logo-image"
                style={{ height: '32px' }}
              />
            </h1>
            <div className="header-right">
              <div className="user-info">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt=""
                    className="user-avatar"
                    referrerPolicy="no-referrer"
                  />
                )}
                <span>{user?.email}</span>
                <span className="role-badge" data-role={user?.role}>{user?.role}</span>
              </div>
              <button onClick={handleLogout} className="logout-button">로그아웃</button>
            </div>
          </div>
        </header>
      )}
      <div className="layout-body">
        {!isPdfMode && (
          <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
              <button 
                className="sidebar-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? '사이드바 확장' : '사이드바 최소화'}
              >
                {isV2 ? (sidebarCollapsed ? '›' : '‹') : (sidebarCollapsed ? '→' : '←')}
              </button>
            </div>
            <nav className="sidebar-nav">
              <div className="nav-section">
                <h3 className="nav-section-title">대시보드</h3>
                <ul className="nav-menu">
                  <li>
                    <button
                      className={location.pathname === '/dashboard' || location.pathname === '/dashboard/apt' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/apt')}
                      title="악성메일 모의 훈련"
                    >
                      <NavIcon isV2={isV2} v2={<SvgMail />} v1="📧" />
                      <span className="menu-text">악성메일 모의 훈련</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={location.pathname === '/dashboard/edr-threat' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/edr-threat')}
                      title="EDR 위협탐지 안내"
                    >
                      <NavIcon isV2={isV2} v2={<SvgShield />} v1="🔍" />
                      <span className="menu-text">EDR 위협탐지 안내</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div className="nav-section">
                <h3 className="nav-section-title">운영보고서</h3>
                <ul className="nav-menu">
                  <li>
                    <button
                      className={location.pathname === '/dashboard/dlp' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/dlp')}
                      title="DLP 운영보고서"
                    >
                      <NavIcon isV2={isV2} v2={<SvgShield />} v1="🔒" />
                      <span className="menu-text">DLP 운영보고서</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={location.pathname === '/dashboard/nac' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/nac')}
                      title="NAC 운영보고서"
                    >
                      <NavIcon isV2={isV2} v2={<SvgNetwork />} v1="🛡️" />
                      <span className="menu-text">NAC 운영보고서</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={location.pathname === '/dashboard/web-filter' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/web-filter')}
                      title="유해차단 운영보고서"
                    >
                      <NavIcon isV2={isV2} v2={<SvgBan />} v1="🚫" />
                      <span className="menu-text">유해차단 운영보고서</span>
                    </button>
                  </li>
                  {/* 월간 운영보고서끼리 붙여 둔다 — 일일보고서는 단위가 달라 뒤에 온다 */}
                  <li>
                    <button
                      className={location.pathname === '/dashboard/edr' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/edr')}
                      title="EDR 운영보고서"
                    >
                      <NavIcon isV2={isV2} v2={<SvgShield />} v1="🔍" />
                      <span className="menu-text">EDR 운영보고서</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={location.pathname === '/dashboard/daily-report' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/daily-report')}
                      title="일일보고서"
                    >
                      <NavIcon isV2={isV2} v2={<SvgDoc />} v1="📋" />
                      <span className="menu-text">일일보고서</span>
                    </button>
                  </li>
                </ul>
              </div>
              {isAiChatEnabled && (
                <div className="nav-section">
                  <h3 className="nav-section-title">정보보호 AI</h3>
                  <ul className="nav-menu">
                    <li>
                      <button
                        className={location.pathname === '/ai-chat' ? 'active' : ''}
                        onClick={() => navigate('/ai-chat')}
                        title="AI 챗봇 소개"
                      >
                        <NavIcon isV2={isV2} v2={<SvgBot />} v1="🤖" />
                        <span className="menu-text">AI 챗봇 소개</span>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              {(user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
                <div className="nav-section">
                  <h3 className="nav-section-title">관리</h3>
                  <ul className="nav-menu">
                    <li>
                      <button
                        className={`nav-menu-parent ${isAdminPage ? 'active' : ''} ${adminMenuExpanded ? 'expanded' : ''}`}
                        onClick={() => {
                          setAdminMenuExpanded(!adminMenuExpanded);
                          if (!isAdminPage) {
                            navigate('/admin?category=USER');
                          }
                        }}
                        title="관리자 페이지"
                      >
                        <NavIcon isV2={isV2} v2={<SvgSettings />} v1="⚙️" />
                        <span className="menu-text">관리자 페이지</span>
                        <span className="menu-arrow">{adminMenuExpanded ? '▼' : '▶'}</span>
                      </button>
                      {adminMenuExpanded && !sidebarCollapsed && (
                        <ul className="nav-submenu">
                          <li>
                            <button
                              className={adminCategory === 'USER' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=USER')}
                              title="유저관리"
                            >
                              <NavIcon isV2={isV2} v2={<SvgUser />} v1="👤" />
                              <span className="menu-text">USER</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'APT' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=APT')}
                              title="APT 스팸신고데이터"
                            >
                              <NavIcon isV2={isV2} v2={<SvgMail />} v1="📧" />
                              <span className="menu-text">APT</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'DLP' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=DLP&tab=dlp-report')}
                              title="DLP 운영보고서 관리"
                            >
                              <NavIcon isV2={isV2} v2={<SvgShield />} v1="🔒" />
                              <span className="menu-text">DLP</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'NAC' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=NAC&tab=nac-report')}
                              title="NAC 운영보고서 관리"
                            >
                              <NavIcon isV2={isV2} v2={<SvgNetwork />} v1="🛡️" />
                              <span className="menu-text">NAC</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'WEB_FILTER' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=WEB_FILTER&tab=wf-report')}
                              title="유해차단 운영보고서 관리"
                            >
                              <NavIcon isV2={isV2} v2={<SvgBan />} v1="🚫" />
                              <span className="menu-text">유해차단</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'EDR' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=EDR&tab=edr-html')}
                              title="EDR 위협탐지 HTML 등록 · 운영보고서 · 라이선스 관리"
                            >
                              <NavIcon isV2={isV2} v2={<SvgShield />} v1="🔍" />
                              <span className="menu-text">EDR</span>
                            </button>
                          </li>
                          {user?.role === 'admin' && (
                            <li>
                              <button
                                className={adminCategory === 'EXCEPTION' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=EXCEPTION&tab=exception-security')}
                                title="보안 솔루션 예외처리 관리"
                              >
                                <NavIcon isV2={isV2} v2={<SvgBan />} v1="🚫" />
                                <span className="menu-text">예외처리</span>
                              </button>
                            </li>
                          )}
                          {(user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
                            <li>
                              <button
                                className={adminCategory === 'META' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=META')}
                                title="DLP 메타 매핑 관리 (소프트웨어·웹사이트)"
                              >
                                <NavIcon isV2={isV2} v2={<SvgDoc />} v1="🏷️" />
                                <span className="menu-text">메타 매핑</span>
                              </button>
                            </li>
                          )}
                          {user?.role === 'admin' && (
                            <li>
                              <button
                                className={adminCategory === 'DAILY_REPORT' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=DAILY_REPORT')}
                                title="일일보고서 데이터 관리"
                              >
                                <NavIcon isV2={isV2} v2={<SvgDoc />} v1="📅" />
                                <span className="menu-text">일일보고서</span>
                              </button>
                            </li>
                          )}
                          {(user?.role === 'admin' || user?.role === 'editor') && (
                            <li>
                              <button
                                className={adminCategory === 'REPORT_REVIEW' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=REPORT_REVIEW')}
                                title="작성/검토이력 (월별 작성자·승인자)"
                              >
                                <NavIcon isV2={isV2} v2={<SvgDoc />} v1="📝" />
                                <span className="menu-text">작성/검토이력</span>
                              </button>
                            </li>
                          )}
                          {user?.role === 'admin' && (
                            <li>
                              <button
                                className={adminCategory === 'LOG' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=LOG')}
                                title="활동 로그"
                              >
                                <NavIcon isV2={isV2} v2={<SvgLog />} v1="📋" />
                                <span className="menu-text">LOG</span>
                              </button>
                            </li>
                          )}
                          {user?.role === 'admin' && (
                            <li>
                              <button
                                className={adminCategory === 'SETTINGS' ? 'active' : ''}
                                onClick={() => navigate('/admin?category=SETTINGS')}
                                title="페이지 설정"
                              >
                                <NavIcon isV2={isV2} v2={<SvgSettings />} v1="🛠️" />
                                <span className="menu-text">페이지 설정</span>
                              </button>
                            </li>
                          )}
                          {user?.role === 'admin' && (
                            <li>
                              <button
                                className={location.pathname === '/admin/ai-chat' ? 'active' : ''}
                                onClick={() => navigate('/admin/ai-chat')}
                                title="AI 챗봇 문서"
                              >
                                <NavIcon isV2={isV2} v2={<SvgBot />} v1="🤖" />
                                <span className="menu-text">AI 챗봇 문서</span>
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </nav>
          </aside>
        )}
        <main className="main-content">
          <Outlet context={{ selectedRound }} />
        </main>
      </div>
      {!isPdfMode && <Footer />}
    </div>
  );
};

export default Layout;

